/* eslint-disable @typescript-eslint/comma-dangle */
/* eslint-disable react/no-unused-prop-types */
import { SxProps, Theme } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import * as React from 'react';
import { EventActionsEnum, EventCategoriesEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import IconButton from '../Buttons/IconButton';

interface IIconMenuDropdownProps<T> {
    name: string;
    iconButton: React.ReactElement;
    options: { label: JSX.Element | string; value: T; isReadOnly?: boolean }[];
    handleSelection: (value: T) => void;
    messageToDisplayAtTop?: string;
    buttonClassName?: string;
    menuStyle?: SxProps<Theme>;
    tooltip?: string;
    testId?: string | undefined;
    track: TrackingEvent;
}

const IconMenuDropdown = <T,>(props: IIconMenuDropdownProps<T>): JSX.Element => {
    const [anchorElement, setAnchorElement] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorElement);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElement(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorElement(null);
        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.IconMenuClosed, '', props.name);
        trackingService.track({ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}Closed` });
    };

    const handleOptionChange = (label: JSX.Element | string): void => {
        const option = props.options.find((x) => x.label === label);

        if (option) {
            loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.IconMenuSelected, `${option.value}`, props.name);
            props.handleSelection(option.value);
        }

        setAnchorElement(null);
    };

    const capitalizeFirstLetter = (value: string | undefined): string => {
        if (!value) {
            return '';
        }
        return value.charAt(0).toUpperCase() + value.slice(1);
    };

    return (
        <div>
            <IconButton
                className={props.buttonClassName}
                handleClick={handleClick}
                tooltip={props.tooltip}
                testId={props.testId}
                track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}Opened` }}
            >
                {props.iconButton}
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorElement}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                    sx: props.menuStyle,
                }}
            >
                {props.messageToDisplayAtTop && (
                    <MenuItem value="" disabled disableRipple disableTouchRipple sx={{ color: 'black' }} data-testid={`${props.testId}OptionDefault`}>
                        <em>{props.messageToDisplayAtTop}</em>
                    </MenuItem>
                )}
                {props.options.map((option) => {
                    if (option.isReadOnly) {
                        return (
                            <MenuItem
                                key={`${option.value}`}
                                sx={{
                                    width: '100%',
                                    padding: 0,
                                    display: 'block',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                                disableRipple
                                disableTouchRipple
                            >
                                {option.label}
                            </MenuItem>
                        );
                    }

                    return (
                        <MenuItem
                            key={`${option.value}`}
                            value={`${option.value}`}
                            onClick={() => handleOptionChange(option.label)}
                            data-testid={`${props.testId}Option${capitalizeFirstLetter(option.value as unknown as string)}`}
                        >
                            {typeof option.label === 'string' ? option.label.charAt(0).toUpperCase() + option.label.slice(1) : option.label}
                        </MenuItem>
                    );
                })}
            </Menu>
        </div>
    );
};

export default IconMenuDropdown;
